import { useEffect, useState } from 'react';

import { faGear } from '@fortawesome/free-solid-svg-icons';
import { Trans } from '@lingui/react/macro';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { format } from 'date-fns';

import { ToolbarButton } from '@/modules/annotator/imageToolbar/ToolbarButton';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { useGetRecentVisits } from '../../../../react-query/useGetRecentVisits';
import { type AdjustmentVersion } from '../../../../react-query/useGetSharpenedImages';
import { usePreferenceHistory } from '../../../../react-query/usePreferenceHistory';
import {
  usePreferences,
  usePreferencesPracticeUpdate,
} from '../../../../react-query/usePreferences';
import { VisitSelector } from '../VisitSelector';

import { FilterComparisonPreview } from './FilterComparisonPreview';
import { adjustmentVersionLabel, getDefaultFilterInfo } from './helpers';
import { ImageCarousel } from './ImageCarousel';

// Modal dialog for configuring sharpening filter
export const SharpnessFilterSelectionModal = ({
  isInImageToolbar = false,
}: {
  isInImageToolbar?: boolean;
}) => {
  // Local state for POC
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<number | undefined>(
    undefined,
  );
  const [selectedPatientId, setSelectedPatientId] = useState<
    string | undefined
  >(undefined);
  const [selectedImageId, setSelectedImageId] = useState<string>('');
  const [leftFilter, setLeftFilter] = useState<AdjustmentVersion | null>(null);
  const [finalFilter, setFinalFilter] = useState<AdjustmentVersion | null>(
    null,
  );
  const [isHD1Selected, setIsHD1Selected] = useState(true);

  const { data: preferences } = usePreferences();
  const defaultFilterInfo = getDefaultFilterInfo(preferences, isHD1Selected);

  // Only depend on the preference for the *current* HD so a change to the other HD's preference (e.g. refetch) doesn't reset in-progress selection
  const currentDefaultFilter = defaultFilterInfo.current;

  // Default to the filter for the selected HD (single-HD: always adjustmentVersion; multiple HD versions: use that slot's preference)
  useEffect(() => {
    setLeftFilter(currentDefaultFilter);
    setFinalFilter(currentDefaultFilter);
  }, [isOpen, isHD1Selected, currentDefaultFilter]);

  const preferencesUpdate = usePreferencesPracticeUpdate();

  const recentVisitsQuery = useGetRecentVisits();

  const { trackEvent } = useTrackEvent();

  const availableSharpeningFilters = preferences?.availableSharpeningFilters;

  const preferenceHistory = usePreferenceHistory({
    preferenceKey: isHD1Selected ? 'adjustmentVersion' : 'adjustmentVersionHD2',
  });

  const lastUpdatedDate = preferenceHistory.data?.updatedAt
    ? format(new Date(preferenceHistory.data.updatedAt), 'MM/dd/yyyy')
    : undefined;
  const lastUpdatedBy =
    preferenceHistory.data?.user &&
    `${preferenceHistory.data.user.firstName} ${preferenceHistory.data.user.lastName}`;

  const isSaveDisabled =
    !selectedImageId ||
    finalFilter === null ||
    (finalFilter === preferences?.adjustmentVersion && isHD1Selected) ||
    (finalFilter === preferences?.adjustmentVersionHD2 && !isHD1Selected);

  const selectedHDVersion = isHD1Selected ? 'HD1' : 'HD2';

  const handleSaveFilter = () => {
    if (isHD1Selected && finalFilter !== null) {
      preferencesUpdate.mutate(
        {
          adjustmentVersion: finalFilter,
        },
        {
          onSuccess: () => {
            trackEvent(mixpanelEvents.sharpeningFilterSelection, {
              filter: finalFilter,
              version: 'HD1',
            });
          },
        },
      );
    } else if (!isHD1Selected && finalFilter !== null) {
      preferencesUpdate.mutate(
        {
          adjustmentVersionHD2: finalFilter,
        },
        {
          onSuccess: () => {
            trackEvent(mixpanelEvents.sharpeningFilterSelection, {
              filter: finalFilter,
              version: 'HD2',
            });
          },
        },
      );
    }
  };

  const handleVisitChange = (visitId: string) => {
    const patientId =
      recentVisitsQuery.data?.visits?.find(
        (visit) => visit.id === Number(visitId),
      )?.patientVideaId || '';
    setSelectedVisit(Number(visitId));
    setSelectedPatientId(patientId);
    setSelectedImageId('');
  };

  return (
    <>
      {isInImageToolbar ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ToolbarButton
                icon={faGear}
                isActive={isOpen}
                onClick={() => {
                  setIsOpen(true);
                  trackEvent(
                    mixpanelEvents.sharpeningFilterSelectionModalOpen,
                    {
                      source: 'imageToolbar',
                    },
                  );
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="top"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>
              <Trans>Configure Sharpening Filter</Trans>
            </p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            setIsOpen(true);
            trackEvent(mixpanelEvents.sharpeningFilterSelectionModalOpen, {
              source: 'practicePreferencesMenu',
            });
          }}
        >
          <Trans>Configure Sharpening Filter</Trans>
        </Button>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="vai-max-w-6xl vai-max-h-[95vh] vai-overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Trans>Image Sharpening Configuration</Trans>
            </DialogTitle>
          </DialogHeader>

          <div className="vai-flex vai-flex-col vai-gap-4 vai-w-full">
            {/* Instructions */}
            <div className="vai-flex vai-flex-col vai-gap-2">
              <div className="vai-flex vai-items-center vai-gap-2 vai-text-gray-600 vai-text-sm">
                <Trans>
                  Select a recent visit, choose an image, and compare different
                  sharpening filters side-by-side to determine your preferred
                  setting.
                </Trans>
              </div>
              <div className="vai-p-3 vai-rounded-md vai-bg-orange-50 vai-border vai-border-orange-200 vai-mt-2">
                <div className="vai-text-orange-800 vai-text-sm">
                  <Trans>
                    Note: This update will only affect images taken starting now
                    and will not automatically rerun all historical images. You
                    can manually rerun individual images if needed.
                  </Trans>
                </div>
              </div>
            </div>

            {/* Step 1: Visit Selection */}
            <VisitSelector
              recentVisitsQuery={recentVisitsQuery}
              selectedVisit={selectedVisit}
              onVisitChange={handleVisitChange}
            />

            {/* Step 2: Image Carousel (only show if visit selected) */}
            {selectedVisit && selectedPatientId && (
              <ImageCarousel
                visitId={selectedVisit}
                patientId={selectedPatientId}
                selectedImageId={selectedImageId}
                onImageSelect={setSelectedImageId}
              />
            )}

            {/* Step 3: Side-by-Side Filter Comparison (only show if image selected) */}
            {selectedImageId &&
              selectedVisit &&
              selectedPatientId &&
              preferences?.enableSharpeningHd2Selection && (
                <div className="vai-flex vai-flex-col vai-gap-2">
                  <div>
                    <Trans>Step 3: Compare Filters</Trans>
                  </div>

                  <div className="vai-text-sm">
                    <Trans>
                      Choose the filter you&apos;d like to compare to the
                      original. You&apos;ll review side-by-side in the next
                      step.
                    </Trans>
                  </div>

                  <div className="vai-w-fit">
                    <Select
                      value={selectedHDVersion}
                      onValueChange={(v: 'HD1' | 'HD2') =>
                        setIsHD1Selected(v === 'HD1')
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['HD1', 'HD2']?.map((filter) => (
                          <SelectItem key={filter} value={filter}>
                            <span className="vai-mr-2">
                              {filter === 'HD1' ? (
                                <Trans>HD1 Filter</Trans>
                              ) : (
                                <Trans>HD2 Filter</Trans>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

            {selectedImageId && selectedVisit && selectedPatientId && (
              <FilterComparisonPreview
                leftFilter={leftFilter}
                rightFilter={finalFilter}
                imageId={selectedImageId}
                onLeftFilterChange={setLeftFilter}
                onRightFilterChange={setFinalFilter}
                visitId={selectedVisit}
                patientId={selectedPatientId}
                availableSharpeningFilters={availableSharpeningFilters}
                isHD1Selected={
                  preferences?.enableSharpeningHd2Selection
                    ? isHD1Selected
                    : true
                }
              />
            )}

            {selectedImageId &&
              selectedVisit &&
              selectedPatientId &&
              preferenceHistory.data?.value && (
                <span className="vai-flex vai-text-sm vai-gap-6 vai-border-[1px] vai-border-zinc-200 vai-bg-zinc-100 vai-text-zinc-500 vai-p-2 vai-rounded-md">
                  <span>
                    <Trans>Current</Trans>
                    <span> {isHD1Selected ? 'HD1' : 'HD2'} </span>
                    <Trans>Profile: </Trans>
                    <span>
                      {
                        adjustmentVersionLabel(
                          preferenceHistory.data?.value as AdjustmentVersion,
                        ).label
                      }
                    </span>
                  </span>
                  <span>
                    <Trans>Last updated: </Trans>
                    <span>{lastUpdatedDate}</span>
                    {lastUpdatedBy && (
                      <>
                        <Trans> by </Trans>
                        <span>{lastUpdatedBy}</span>
                      </>
                    )}
                  </span>
                </span>
              )}

            {/* Action Buttons */}
            <div className="vai-flex vai-gap-3 vai-pt-4 vai-justify-end">
              <div className="vai-flex vai-gap-3">
                <Button variant="outline" onClick={() => setIsOpen(false)}>
                  <Trans>Cancel</Trans>
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        onClick={handleSaveFilter}
                        disabled={isSaveDisabled}
                      >
                        <span className="vai-flex vai-items-center vai-gap-1">
                          <Trans>Save </Trans>
                          {preferences?.enableSharpeningHd2Selection
                            ? selectedHDVersion
                            : 'HD'}
                          <Trans> Profile</Trans>
                        </span>
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {isSaveDisabled && !!selectedImageId && (
                    <TooltipContent side="top" className="vai-max-w-48">
                      {finalFilter === null ? (
                        <Trans>No filter is selected</Trans>
                      ) : (
                        <Trans>
                          Selected filter is the same as the current setting
                        </Trans>
                      )}
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>
            </div>
            {!selectedImageId && (
              <div className="vai-text-right vai-text-gray-600">
                <Trans>Select a visit and image to continue</Trans>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
